import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  InputBase
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import Papers from '@mui/material/Paper';
import { styled } from "@mui/system";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

// Memoized styled components to prevent recreation
const StyledSidebar = styled(Box)(({ theme }) => ({
  width: props => props.isMobile ? "100%" : '300px',
  borderRight: "1px solid #ddd",
  borderRadius: 5,
  overflowY: "auto",
  background: "#ffffff"
}));

// SearchInput Component with optimization
const SearchInput = React.memo(({ inputValue, onChange }) => {
  // Use useCallback to prevent recreation of handleChange on each render
  const handleChange = useCallback((e) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <Box p={2}>
      <Papers component="form" style={{
        display: "flex",
        flexDirection: 'row',
        backgroundColor: "white",
        border: '1px solid #000000',
        width: '100%',
      }}>
        <IconButton type="button" aria-label="search">
          <SearchIcon style={{ color: '#000000' }} />
        </IconButton>
        <InputBase
          placeholder="Search contacts"
          autoFocus={inputValue.length > 0}
          value={inputValue}
          onChange={handleChange}
          style={{
            color: '#000000'
          }}
          fullWidth />
      </Papers>
    </Box>
  );
});

// Extract message rendering to separate component for more focused rerenders
const LastMessage = React.memo(({ lastMessage }) => {
  return (
    <Typography variant="body2" sx={{ color: "#666666" }}>
      {lastMessage === 'no message' ? t('noMessages') : lastMessage}
    </Typography>
  );
});

// ContactItem Component to reduce RenderList rendering load
const ContactItem = React.memo(({ contact, isSelected, onClick }) => {
  return (
    <ListItem
      button
      sx={{
        background: isSelected ? "#e0e0e0" : "#ffffff",
        "&:hover": { background: "#f5f5f5" }
      }}
      onClick={onClick}
    >
      <ListItemAvatar>
        <Avatar src={`${process.env.REACT_APP_UPLOADS_PATH + contact.avatar}`} alt={contact.name} />
      </ListItemAvatar>
      <ListItemText
        primary={<Typography variant="subtitle1">{contact.name}</Typography>}
        secondary={<LastMessage lastMessage={contact.lastMessage} />}
      />
    </ListItem>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to only rerender when necessary
  return (
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.contact.id === nextProps.contact.id &&
    prevProps.contact.name === nextProps.contact.name &&
    prevProps.contact.avatar === nextProps.contact.avatar &&
    prevProps.contact.lastMessage === nextProps.contact.lastMessage
  );
});

// RenderList Component with optimization
const RenderList = React.memo(({ contacts, selectedChatroom, handleContactClick }) => {
  // Optimize contact click handling by creating handlers only once per contact
  const contactHandlers = useMemo(() => {
    const handlers = {};
    contacts.forEach(contact => {
      handlers[contact.id] = () => handleContactClick(contact);
    });
    return handlers;
  }, [contacts, handleContactClick]);

  return (
    <List>
      {contacts.map((contact) => (
        <ContactItem
          key={contact.id}
          contact={contact}
          isSelected={selectedChatroom?.id === contact.id}
          onClick={contactHandlers[contact.id]}
        />
      ))}
    </List>
  );
}, (prevProps, nextProps) => {
  // Only rerender if selected chatroom changes or contacts change
  if (prevProps.selectedChatroom?.id !== nextProps.selectedChatroom?.id) {
    return false;
  }
  
  if (prevProps.contacts.length !== nextProps.contacts.length) {
    return false;
  }
  
  // Check if any contact's last message has changed
  for (let i = 0; i < prevProps.contacts.length; i++) {
    if (prevProps.contacts[i].lastMessage !== nextProps.contacts[i].lastMessage) {
      return false;
    }
  }
  
  return true;
});

// Custom Hook for Mobile Check
const useMediaQuery = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const updateMedia = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  }, []);

  return isMobile;
};

const ChatRoomUI = React.memo(({ handleContactClick, selectedChatroom, contacts, setContacts }) => {
  const [inputValue, setInputValue] = useState('');
  const [filterContacts, setFilterContacts] = useState([]);
  const isMobile = useMediaQuery();
  const { t } = useTranslation();

  // Memoize search change handler
  const handleSearchChange = useCallback((searchQuery) => {
    setInputValue(searchQuery);
  }, []);

  // Filter contacts whenever `inputValue` or contacts change
  useEffect(() => {
    if (inputValue === '') {
      setFilterContacts(contacts);
    } else {
      const filtered = contacts.filter(contact =>
        contact.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        contact.lastMessage.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilterContacts(filtered);
    }
  }, [inputValue, contacts]);

  // Memoize filtered contacts to prevent unnecessary rerenders
  const memoizedFilteredContacts = useMemo(() => filterContacts, [filterContacts]);

  return (
    <StyledSidebar isMobile={isMobile} width={isMobile ? '100%' : '300px'}>
      <SearchInput 
        inputValue={inputValue} 
        onChange={handleSearchChange} 
      />
      <RenderList 
        contacts={memoizedFilteredContacts} 
        selectedChatroom={selectedChatroom} 
        handleContactClick={handleContactClick} 
      />
    </StyledSidebar>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to only update when necessary
  if (prevProps.selectedChatroom?.id !== nextProps.selectedChatroom?.id) {
    return false; // Changed selected chatroom, need to rerender
  }
  
  if (prevProps.contacts.length !== nextProps.contacts.length) {
    return false; // Number of contacts changed, need to rerender
  }

  // Check if any contact's last message has changed
  for (let i = 0; i < prevProps.contacts.length; i++) {
    if (prevProps.contacts[i].id === nextProps.contacts[i].id) {
      if (prevProps.contacts[i].lastMessage !== nextProps.contacts[i].lastMessage) {
        return false; // Last message changed, need to rerender
      }
    } else {
      return false; // Contact ID changed, need to rerender
    }
  }
  
  return true; // No changes, don't rerender
});

export default React.memo(ChatRoomUI);
